import { Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup, IconButton, LinearProgress, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import React from 'react';
import useOwnerNames from '../../hooks/useOwnerNames';

export interface ImageCreateParams {
  owner: string | null;
  platform: string;
  piHost: string;
  options: {
    fullUpdate: boolean;
    enableBluetooth: boolean;
    enableZigbee: boolean;
    clearHistory: boolean;
    enableReadOnly: boolean;
    restoreDevPi: boolean;
    resumeFromExisting: boolean;
  };
}

export interface LogLine {
  timestamp: number;
  step: string;
  percent: number;
  detail: string;
}

interface CreateImageDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (params: ImageCreateParams) => void;
  progress: { step: string; percent: number; detail: string } | null;
  logLines: LogLine[];
  startTime: number | null;
  buildDone: boolean;
  onClearLog: () => void;
  wsConnected: boolean;
  lastMessageAt: number | null;
}

function formatDuration(ms: number): string {
  if (ms < 0 || !Number.isFinite(ms)) return '--:--';
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

function CreateImageDialog({ open, onClose, onCreate, progress, logLines, startTime, buildDone, onClearLog, wsConnected, lastMessageAt }: CreateImageDialogProps) {
  const { ownerNames } = useOwnerNames();
  const [imageType, setImageType] = React.useState<string>('update');
  const [owner, setOwner] = React.useState<string | null>(null);
  const [platform, setPlatform] = React.useState('PI4');
  const [piHost, setPiHost] = React.useState(() => localStorage.getItem('iot-builder-piHost') || '192.168.0.100');
  const [fullUpdate, setFullUpdate] = React.useState(true);
  const [enableBluetooth, setEnableBluetooth] = React.useState(false);
  const [enableZigbee, setEnableZigbee] = React.useState(false);
  const [clearHistory, setClearHistory] = React.useState(true);
  const [enableReadOnly, setEnableReadOnly] = React.useState(true);
  const [restoreDevPi, setRestoreDevPi] = React.useState(true);
  const [resumeFromExisting, setResumeFromExisting] = React.useState(false);

  const [now, setNow] = React.useState(Date.now());
  const logScrollRef = React.useRef<HTMLDivElement | null>(null);

  const running = startTime !== null && !buildDone;

  // Baseline total build time for the countdown, computed from the currently-selected
  // options. Empirical: 12 min core cycle, +2 min for fullUpdate, +3 min for zero free space.
  // Resume path is just checksum + upload, so a short baseline.
  const baselineMs = React.useMemo(() => {
    if (resumeFromExisting) return 2 * 60 * 1000;
    let ms = 15 * 60 * 1000;
    if (fullUpdate) ms += 2 * 60 * 1000;
    return ms;
  }, [resumeFromExisting, fullUpdate]);

  React.useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  React.useEffect(() => {
    if (logScrollRef.current) {
      logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight;
    }
  }, [logLines.length]);

  const elapsed = startTime ? now - startTime : 0;
  const percent = progress?.percent ?? 0;
  const remainingMs = running ? Math.max(0, baselineMs - elapsed) : null;

  // Stale detection: no progress/log message in > 3 min while running = something's wrong
  const STALE_THRESHOLD_MS = 3 * 60 * 1000;
  const msSinceLastMessage = lastMessageAt ? now - lastMessageAt : 0;
  const isStale = running && lastMessageAt !== null && msSinceLastMessage > STALE_THRESHOLD_MS;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (running) return;
    if (imageType === 'owner' && !owner) return;
    if (!resumeFromExisting && !piHost.trim()) return;
    if (piHost.trim()) localStorage.setItem('iot-builder-piHost', piHost.trim());
    onCreate({
      owner: imageType === 'owner' ? owner : null,
      platform,
      piHost: piHost.trim(),
      options: { fullUpdate, enableBluetooth, enableZigbee, clearHistory, enableReadOnly, restoreDevPi, resumeFromExisting },
    });
  };

  const handleCopy = () => {
    const text = logLines.map(l => `${formatTime(l.timestamp)}  [${String(l.percent).padStart(3, ' ')}%]  ${l.step.padEnd(10)}  ${l.detail}`).join('\n');
    navigator.clipboard.writeText(text);
  };

  const showLog = startTime !== null;

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth={showLog ? 'md' : 'xs'} fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 3 }}>
          <span>Create Image</span>
          {showLog && (
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              Elapsed {formatDuration(elapsed)}
              {remainingMs !== null && ` · Remaining ${formatDuration(remainingMs)}`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', gap: 2, pb: 1 }}>
          <Box sx={{ flex: showLog ? '0 0 280px' : 1, minWidth: 0 }}>
            <TextField
              select
              margin="dense"
              label="Image Type"
              fullWidth
              size="small"
              value={imageType}
              onChange={(e) => { setImageType(e.target.value); if (e.target.value === 'update') setOwner(null); }}
              disabled={running}
            >
              <MenuItem value="update">OS Update (rootfs only)</MenuItem>
              <MenuItem value="owner">Owner (full card + .env)</MenuItem>
            </TextField>

            {imageType === 'owner' && (
              <Autocomplete
                options={ownerNames}
                value={owner}
                onChange={(_e, value) => setOwner(value)}
                disabled={running}
                renderInput={(params) => (
                  <TextField {...params} margin="dense" label="Owner" size="small" required />
                )}
                sx={{ mt: 1 }}
              />
            )}

            <TextField
              select
              margin="dense"
              label="Platform"
              fullWidth
              size="small"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              disabled={running}
              sx={{ mt: 1 }}
            >
              <MenuItem value="PI4">PI4</MenuItem>
              <MenuItem value="PI5">PI5</MenuItem>
            </TextField>

            <TextField
              margin="dense"
              label="Pi IP Address"
              fullWidth
              size="small"
              value={piHost}
              onChange={(e) => setPiHost(e.target.value)}
              placeholder="192.168.0.x"
              required={!resumeFromExisting}
              disabled={running || resumeFromExisting}
              sx={{ mt: 1 }}
            />

            <FormGroup sx={{ mt: 2 }}>
              <FormControlLabel control={<Checkbox checked={resumeFromExisting} onChange={(e) => setResumeFromExisting(e.target.checked)} size="small" disabled={running} />} label="Resume from existing local image" />
              {resumeFromExisting && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5, mb: 0.5, display: 'block' }}>
                  Skips SSH, prep, dd, pishrink — just re-uploads the existing local file. All options below are ignored.
                </Typography>
              )}
              <FormControlLabel control={<Checkbox checked={fullUpdate} onChange={(e) => setFullUpdate(e.target.checked)} size="small" disabled={running || resumeFromExisting} />} label="Full OS update" />
              {imageType === 'owner' && (
                <>
                  <FormControlLabel control={<Checkbox checked={enableBluetooth} onChange={(e) => setEnableBluetooth(e.target.checked)} size="small" disabled={running || resumeFromExisting} />} label="Enable Bluetooth" />
                  <FormControlLabel control={<Checkbox checked={enableZigbee} onChange={(e) => setEnableZigbee(e.target.checked)} size="small" disabled={running || resumeFromExisting} />} label="Enable Zigbee" />
                </>
              )}
              <FormControlLabel control={<Checkbox checked={clearHistory} onChange={(e) => setClearHistory(e.target.checked)} size="small" disabled={running || resumeFromExisting} />} label="Clear bash history" />
              {imageType === 'owner' && (
                <FormControlLabel control={<Checkbox checked={enableReadOnly} onChange={(e) => setEnableReadOnly(e.target.checked)} size="small" disabled={running || resumeFromExisting} />} label="Enable read-only protection" />
              )}
              <FormControlLabel control={<Checkbox checked={restoreDevPi} onChange={(e) => setRestoreDevPi(e.target.checked)} size="small" disabled={running || resumeFromExisting} />} label="Restore dev Pi after imaging" />
            </FormGroup>
          </Box>

          {showLog && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {progress?.detail || (buildDone ? 'Done' : 'Starting...')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', flex: '0 0 auto' }}>
                    {percent}%
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Copy logs">
                      <IconButton size="small" onClick={handleCopy}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Clear logs">
                      <IconButton size="small" onClick={onClearLog} disabled={running}><ClearAllIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <LinearProgress variant="determinate" value={percent} sx={{ mb: 1 }} />
                {running && !wsConnected && (
                  <Box sx={{ mb: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'warning.dark', color: 'warning.contrastText', fontSize: '0.75rem' }}>
                    ⚠ Connection lost — reconnecting. Build is still running on the builder; progress will resume shortly.
                  </Box>
                )}
                {running && wsConnected && isStale && (
                  <Box sx={{ mb: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'warning.dark', color: 'warning.contrastText', fontSize: '0.75rem' }}>
                    ⚠ No progress update in {Math.round(msSinceLastMessage / 1000)}s — builder may be blocked or disconnected.
                  </Box>
                )}
                <Box
                  ref={logScrollRef}
                  contentEditable
                  suppressContentEditableWarning
                  onBeforeInput={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    // Allow navigation/copy, block edits
                    const allowed = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
                    if (allowed.includes(e.key)) return;
                    if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'a')) return;
                    e.preventDefault();
                  }}
                  sx={{
                    flex: 1,
                    minHeight: 300,
                    maxHeight: 400,
                    overflowY: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre',
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: '#1e1e1e',
                    color: '#e8e8e8',
                    outline: 'none',
                    cursor: 'text',
                    userSelect: 'text',
                  }}
                >
                  {logLines.map((l, i) => (
                    <div key={i}>
                      {formatTime(l.timestamp)}  [{String(l.percent).padStart(3, ' ')}%]  {l.step.padEnd(10)}  {l.detail}
                    </div>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!running && <Button onClick={onClose}>{buildDone ? 'Close' : 'Cancel'}</Button>}
          {!buildDone && (
            <Button type="submit" variant="contained" disabled={running || (imageType === 'owner' && !owner)}>
              {running ? 'Running...' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CreateImageDialog;
